import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatUploadSummary } from '../../interfaces/ChatInterface';
import { getErrorDetail } from '../../utils';
import useErrorModal from './useErrorModal';

export interface UploadingItem {
  id: string;
  name: string;
}

interface UseChatUploadsParams {
  // Changes whenever the underlying resource identity (eoa/scope) changes, triggering a reload.
  reloadKey: string;
  maxCount: number;
  list: () => Promise<ChatUploadSummary[]>;
  upload: (file: File) => Promise<ChatUploadSummary>;
  remove: (uploadId: string) => Promise<void>;
  loadErrorMessage: string;
  deleteErrorMessage: string;
}

const newUploadingId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const useChatUploads = ({
  reloadKey,
  maxCount,
  list,
  upload,
  remove,
  loadErrorMessage,
  deleteErrorMessage,
}: UseChatUploadsParams) => {
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const [uploads, setUploads] = useState<ChatUploadSummary[]>([]);
  const [uploading, setUploading] = useState<UploadingItem[]>([]);

  const listRef = useRef(list);
  listRef.current = list;
  const uploadRef = useRef(upload);
  uploadRef.current = upload;
  const removeRef = useRef(remove);
  removeRef.current = remove;

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const items = await listRef.current();
        if (active) setUploads(items);
      } catch (error) {
        handleOpenErrorModal(loadErrorMessage, error as Error);
      }
    })();
    return () => {
      active = false;
    };
  }, [reloadKey, loadErrorMessage, handleOpenErrorModal]);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (uploads.length + uploading.length + files.length > maxCount) {
        const message = `You can keep at most ${maxCount} files here. Remove some before adding more.`;
        handleOpenErrorModal(message, new Error(message));
        return;
      }
      for (const file of files) {
        const item: UploadingItem = { id: newUploadingId(), name: file.name };
        setUploading((prev) => [...prev, item]);
        try {
          const created = await uploadRef.current(file);
          setUploads((prev) => [...prev, created]);
        } catch (error) {
          handleOpenErrorModal(getErrorDetail(error, `Failed to upload ${file.name}`), error as Error);
        } finally {
          setUploading((prev) => prev.filter((entry) => entry.id !== item.id));
        }
      }
    },
    [handleOpenErrorModal, maxCount, uploads.length, uploading.length]
  );

  const removeUpload = useCallback(
    async (uploadId: string) => {
      try {
        await removeRef.current(uploadId);
        setUploads((prev) => prev.filter((entry) => entry.id !== uploadId));
      } catch (error) {
        handleOpenErrorModal(deleteErrorMessage, error as Error);
      }
    },
    [handleOpenErrorModal, deleteErrorMessage]
  );

  return { uploads, uploading, uploadFiles, removeUpload };
};

export default useChatUploads;
